import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Client, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { secureStorage } from '../lib/secureStorage';
import { API_BASE_URL } from '../lib/api';
import { useAuth } from './AuthContext';

export interface WebSocketMessage {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  priority: string;
  actionUrl?: string;
  actionLabel?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WebSocketContextType {
  status: ConnectionStatus;
  isConnected: boolean;
  subscribe: (destination: string, callback: (message: WebSocketMessage) => void) => () => void;
  sendMessage: (destination: string, body: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const clientRef = useRef<Client | null>(null);
  const activeSubscriptionsRef = useRef<Map<string, Set<(m: WebSocketMessage) => void>>>(new Map());
  const stompSubscriptionsRef = useRef<Map<string, StompSubscription>>(new Map());

  const disconnect = useCallback(() => {
    if (clientRef.current) {
      console.log('🔌 Deactivating shared WebSocket client...');
      clientRef.current.deactivate();
      clientRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  const connect = useCallback(() => {
    // If client exists and is busy, don't start another
    if (clientRef.current && (clientRef.current.active || clientRef.current.connected)) {
      return;
    }

    const token = secureStorage.getAuthToken();
    if (!token || !user) {
      return;
    }

    console.log('🔌 Initializing shared WebSocket connection...');
    setStatus('connecting');

    const client = new Client({
      webSocketFactory: () => new (SockJS as any)(`${API_BASE_URL}/api/ws-notifications`),
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      // Re-reads the token fresh before EVERY (re)connection attempt, not just the first — stomp.js
      // otherwise reuses the connectHeaders object captured once above, so a token silently refreshed
      // by AuthContext's background timer (see scheduleTokenRefresh) would never reach a client that
      // auto-reconnects after that point, presenting a now-stale Bearer token indefinitely.
      beforeConnect: (activeClient) => {
        const freshToken = secureStorage.getAuthToken();
        if (freshToken) {
          activeClient.connectHeaders = { Authorization: `Bearer ${freshToken}` };
        }
      },
      debug: (str) => {
        if (str.includes('ERROR') || str.includes('Connected') || str.includes('DISCONNECT')) {
          console.log('🔌 Shared WS:', str);
        }
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      onConnect: () => {
        console.log('✅ Shared WebSocket connected');
        setStatus('connected');

        // Re-subscribe to all active destinations after reconnection
        activeSubscriptionsRef.current.forEach((callbacks, destination) => {
          if (!stompSubscriptionsRef.current.has(destination)) {
            const sub = client.subscribe(destination, (msg) => {
              const body = JSON.parse(msg.body);
              callbacks.forEach(cb => cb(body));
            });
            stompSubscriptionsRef.current.set(destination, sub);
          }
        });
      },

      onDisconnect: () => {
        console.log('🔌 Shared WebSocket disconnected');
        setStatus('disconnected');
        stompSubscriptionsRef.current.clear();
      },

      onWebSocketClose: () => {
        setStatus('disconnected');
        stompSubscriptionsRef.current.clear();
      },

      onStompError: (frame) => {
        console.error('❌ STOMP error in shared client:', frame.headers['message']);
        setStatus('error');
      },
      
      onWebSocketError: () => {
        setStatus('error');
      }
    });

    client.activate();
    clientRef.current = client;
  }, [user]);

  const subscribe = useCallback((destination: string, callback: (message: WebSocketMessage) => void) => {
    // Add to active callbacks
    if (!activeSubscriptionsRef.current.has(destination)) {
      activeSubscriptionsRef.current.set(destination, new Set());
    }
    activeSubscriptionsRef.current.get(destination)!.add(callback);

    // If connected, create stomp subscription if not exists
    if (clientRef.current?.connected && !stompSubscriptionsRef.current.has(destination)) {
      const sub = clientRef.current.subscribe(destination, (msg) => {
        const body = JSON.parse(msg.body);
        activeSubscriptionsRef.current.get(destination)?.forEach(cb => cb(body));
      });
      stompSubscriptionsRef.current.set(destination, sub);
    }

    return () => {
      const callbacks = activeSubscriptionsRef.current.get(destination);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          activeSubscriptionsRef.current.delete(destination);
          const sub = stompSubscriptionsRef.current.get(destination);
          if (sub) {
            sub.unsubscribe();
            stompSubscriptionsRef.current.delete(destination);
          }
        }
      }
    };
  }, []);

  const sendMessage = useCallback((destination: string, body: any) => {
    if (clientRef.current?.connected) {
      clientRef.current.publish({ destination, body: JSON.stringify(body) });
    } else {
      console.warn('⚠️ Cannot send message: WS not connected');
    }
  }, []);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [user, connect, disconnect]);

  return (
    <WebSocketContext.Provider value={{ 
      status, 
      isConnected: status === 'connected', 
      subscribe, 
      sendMessage 
    }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
}
